/**
 * Q3 KPI Scorecard page — route /scorecard.
 * Sidebar "Deliver" section; all authenticated users.
 *
 * Layout:
 *   [Header + period filter]
 *   [Page toggle: Scorecard | Readiness]
 *   Scorecard mode:
 *     [Control row: summary toggle | view toggle | export btn | post btn]
 *     [Summary region: HealthBar | HealthHero]
 *     [TrendChart]
 *     [Cards grid | KpiTable]
 *     [MetricDrawer (overlay)]
 *   Readiness mode:
 *     [ReadinessPanel — cohort summary + field coverage + backfill task table]
 */
import { Alert, Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { type ReactElement, useState } from 'react';

import { useScorecard } from '@/hooks/useScorecard';
import type { Metric, ScorecardParams } from '@/types/scorecard';
import { HealthBar } from './HealthBar';
import { HealthHero } from './HealthHero';
import { KpiCard, KpiCardSkeleton } from './KpiCard';
import { KpiTable, KpiTableSkeleton } from './KpiTable';
import { MetricDrawer } from './MetricDrawer';
import { PeriodFilter } from './PeriodFilter';
import { ReadinessPanel } from './ReadinessPanel';
import { SyncControl } from './SyncControl';
import { TrendChart } from './TrendChart';

type SummaryMode = 'bar' | 'hero';
type ViewMode = 'cards' | 'table';
type PageMode = 'scorecard' | 'readiness';

function ToggleButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      sx={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
        px: 1.625,
        minHeight: 44,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 140ms',
        ...(active
          ? { background: 'rgba(217,119,87,0.10)', color: '#D97757' }
          : {
              background: 'transparent',
              color: 'text.disabled',
              '&:hover': { color: 'text.primary' },
            }),
      }}
    >
      {label}
    </Box>
  );
}

function ActionButton({
  label,
  primary,
  onClick,
}: {
  label: string;
  primary?: boolean;
  onClick?: () => void;
}): ReactElement {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
        px: 1.625,
        minHeight: 44,
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'all 140ms',
        ...(primary
          ? {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              border: '1px solid',
              borderColor: 'primary.main',
              '&:hover': { bgcolor: '#f0a37c' },
            }
          : {
              background: 'background.deeper',
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { borderColor: '#353540', color: 'text.primary' },
            }),
      }}
    >
      {label}
    </Box>
  );
}

export function ScorecardPage(): ReactElement {
  const [period, setPeriod] = useState<ScorecardParams>({ preset: 'since_cutoff' });
  const [pageMode, setPageMode] = useState<PageMode>('scorecard');
  const [summaryMode, setSummaryMode] = useState<SummaryMode>('bar');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [activeMetric, setActiveMetric] = useState<Metric | null>(null);

  const { data, isLoading, error, refetch } = useScorecard(period);

  // Format the period subtitle
  const periodSub = data
    ? `Platform team · ${data.period.label} · as of ${new Date(data.generated_at).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`
    : 'Loading…';

  // allMetrics is available for future status-filter functionality
  // const allMetrics = data?.groups.flatMap((g) => g.metrics) ?? [];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: '26px clamp(16px, 3.5vw, 44px) 72px' },
        minHeight: '100vh',
        overflowX: 'hidden',
      }}
    >
      {/* ---- Top bar: title + period filter ---- */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        flexWrap="wrap"
        gap={2.25}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.625rem',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'primary.main',
            }}
          >
            Sherlock · Deliver
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 700,
              letterSpacing: '-0.4px',
              mt: 0.625,
              mb: 0.25,
              color: 'text.primary',
            }}
          >
            Q3 KPI Scorecard
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.8125rem',
              color: 'text.disabled',
            }}
          >
            {periodSub}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          gap={2.25}
        >
          <SyncControl lastSyncedAt={data?.last_synced_at ?? null} onSynced={refetch} />
          <PeriodFilter value={period} onChange={setPeriod} />
        </Stack>
      </Stack>

      {/* ---- Page mode toggle: Scorecard | Readiness ---- */}
      <Stack direction="row" alignItems="center" spacing={1.125} sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.625rem',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            color: 'text.faintest',
          }}
        >
          Page
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            overflow: 'hidden',
            background: 'background.deeper',
          }}
          role="group"
          aria-label="Scorecard page mode"
        >
          <ToggleButton
            label="Scorecard"
            active={pageMode === 'scorecard'}
            onClick={() => setPageMode('scorecard')}
          />
          <ToggleButton
            label="Readiness"
            active={pageMode === 'readiness'}
            onClick={() => setPageMode('readiness')}
          />
        </Box>
      </Stack>

      {/* ---- Readiness view: Mohit's backfill checklist ---- */}
      {pageMode === 'readiness' && <ReadinessPanel params={period} />}

      {pageMode === 'scorecard' && (
        <>
      {/* ---- Control row ---- */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{
          py: 1.5,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 2.5,
        }}
      >
        {/* Summary toggle */}
        <Stack direction="row" alignItems="center" spacing={1.125} flexWrap="wrap">
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.625rem',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: 'text.faintest',
            }}
          >
            Summary
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              overflow: 'hidden',
              background: 'background.deeper',
            }}
            role="group"
            aria-label="Summary display mode"
          >
            <ToggleButton
              label="Compact bar"
              active={summaryMode === 'bar'}
              onClick={() => setSummaryMode('bar')}
            />
            <ToggleButton
              label="Health hero"
              active={summaryMode === 'hero'}
              onClick={() => setSummaryMode('hero')}
            />
          </Box>
        </Stack>

        {/* View toggle + action buttons */}
        <Stack direction="row" alignItems="center" spacing={1.125} flexWrap="wrap">
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.625rem',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: 'text.faintest',
            }}
          >
            View
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              overflow: 'hidden',
              background: 'background.deeper',
            }}
            role="group"
            aria-label="Metric view mode"
          >
            <ToggleButton
              label="Cards"
              active={viewMode === 'cards'}
              onClick={() => setViewMode('cards')}
            />
            <ToggleButton
              label="Table"
              active={viewMode === 'table'}
              onClick={() => setViewMode('table')}
            />
          </Box>
          <ActionButton label="Export to Notion" />
          <ActionButton label="Post to #platform-leads" primary />
        </Stack>
      </Stack>

      {/* ---- Error state ---- */}
      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2.5 }}>
          Failed to load scorecard: {error.message}
        </Alert>
      )}

      {/* ---- Summary region ---- */}
      {isLoading ? (
        <Box sx={{ mb: 2.25 }}>
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: '8px' }} />
        </Box>
      ) : data ? (
        summaryMode === 'bar' ? (
          <HealthBar summary={data.summary} />
        ) : (
          <HealthHero
            summary={data.summary}
            onStatusClick={(status) => {
              // Future: filter cards/table by status
              void status;
            }}
          />
        )
      ) : null}

      {/* ---- Trend chart ---- */}
      {isLoading ? (
        <Box sx={{ mb: 2.75 }}>
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: '8px' }} />
        </Box>
      ) : data ? (
        <TrendChart groups={data.groups} />
      ) : null}

      {/* ---- Section: Delivery ---- */}
      {data?.groups.map((group) => (
        <Box key={group.key} sx={{ mb: 3 }}>
          {/* Section header */}
          <Stack
            direction="row"
            alignItems="baseline"
            gap={1.5}
            sx={{ mb: 1.75, mt: 2.75 }}
          >
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', letterSpacing: '0.6px' }}
            >
              {group.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.625rem',
                color: 'text.faintest',
                letterSpacing: '0.4px',
              }}
            >
              {group.metrics.length} metrics
            </Typography>
            <Box sx={{ flex: 1, height: 1, background: 'divider' }} />
          </Stack>

          {/* Cards or table */}
          {viewMode === 'cards' ? (
            <Grid container spacing={1.75}>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
                      <KpiCardSkeleton />
                    </Grid>
                  ))
                : group.metrics.map((metric) => (
                    <Grid key={metric.key} item xs={12} sm={6} md={4} lg={3}>
                      <KpiCard metric={metric} onClick={setActiveMetric} />
                    </Grid>
                  ))}
            </Grid>
          ) : null}
        </Box>
      ))}

      {/* Table view — all groups in one table */}
      {viewMode === 'table' && !isLoading && data && (
        <KpiTable groups={data.groups} onMetricClick={setActiveMetric} />
      )}
      {viewMode === 'table' && isLoading && <KpiTableSkeleton />}

      {/* Empty state */}
      {!isLoading && !error && !data && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 2,
          }}
        >
          <Typography variant="h5" sx={{ color: 'text.disabled' }}>
            No scorecard data
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: 'text.faintest',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            Run the sync job to generate scorecard data for this period.
          </Typography>
        </Box>
      )}

      {/* Loading skeleton for cards */}
      {isLoading && !data && viewMode === 'cards' && (
        <Grid container spacing={1.75}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
              <KpiCardSkeleton />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ---- MetricDrawer ---- */}
      <MetricDrawer
        metric={activeMetric}
        params={period}
        onClose={() => setActiveMetric(null)}
      />
        </>
      )}
    </Box>
  );
}
